
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { LoadingState, ErrorState, EmptyState, PageHeader } from "@/components/features/states";
import { toast } from "sonner";
import { Check, Crown, Sparkles, Zap, Building2 } from "lucide-react";
import type { SubscriptionPlan } from "@/lib/types";

const PLAN_ICONS: Record<string, React.ElementType> = {
  free: Sparkles,
  merchant: Zap,
  enterprise: Building2,
};

const PLAN_TONES: Record<string, string> = {
  free: "border-border",
  merchant: "border-primary ring-2 ring-primary/20",
  enterprise: "border-amber-400 ring-1 ring-amber-300/40",
};

export function SubscriptionsView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [actingPlan, setActingPlan] = useState<string | null>(null);

  const { data: plansData, isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: () => api.subscriptions.plans(),
  });
  const { data: mineData } = useQuery({
    queryKey: ["subscription-mine"],
    queryFn: () => api.subscriptions.mine(),
  });

  const plans = plansData?.data?.plans ?? [];
  const mySubscription = mineData?.data?.subscription ?? null;
  const myPlanCode = mySubscription?.plan?.code ?? "free";

  const subscribe = async (planCode: string) => {
    if (planCode === myPlanCode) return;
    setActingPlan(planCode);
    const res = await api.subscriptions.subscribe({ planCode, billingCycle });
    setActingPlan(null);
    if (!res.ok) return toast.error(res.error || t("errors.generic"));
    qc.invalidateQueries({ queryKey: ["subscription-mine"] });
    qc.invalidateQueries({ queryKey: ["subscription-plans"] });
    const planName = plans.find((p) => p.code === planCode)?.name ?? planCode;
    toast.success(t("subscriptions.subscribeSuccess", { plan: planName }));
  };

  const cancel = async () => {
    setActingPlan("cancel");
    const res = await api.subscriptions.cancel();
    setActingPlan(null);
    if (!res.ok) return toast.error(res.error || t("errors.generic"));
    qc.invalidateQueries({ queryKey: ["subscription-mine"] });
    toast.success(t("subscriptions.cancelSuccess"));
  };

  if (plansLoading) return <LoadingState label={t("common.loading")} />;
  if (plansError) return <ErrorState message={t("errors.generic")} onRetry={() => qc.invalidateQueries({ queryKey: ["subscription-plans"] })} />;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader title={t("subscriptions.title")} subtitle={t("subscriptions.subtitle")} />

      {/* Billing cycle toggle */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm font-medium ${billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground"}`}>
          {t("subscriptions.perMonth")}
        </span>
        <Switch
          checked={billingCycle === "annual"}
          onCheckedChange={(v) => setBillingCycle(v ? "annual" : "monthly")}
          aria-label={t("subscriptions.billingCycleAria")}
        />
        <span className={`text-sm font-medium ${billingCycle === "annual" ? "text-foreground" : "text-muted-foreground"}`}>
          {t("subscriptions.perYear")}
        </span>
        {billingCycle === "annual" && (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300">
            {t("subscriptions.savePercent")}
          </Badge>
        )}
      </div>

      {/* Current plan banner */}
      {mySubscription && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-3 p-4">
            <Crown className="h-8 w-8 shrink-0 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-semibold">
                {t("subscriptions.yourCurrentPlan")}: {mySubscription.plan?.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {mySubscription.endsAt
                  ? t("subscriptions.activeUntil", { date: new Date(mySubscription.endsAt).toLocaleDateString() })
                  : t("subscriptions.activeUntil", { date: "—" })}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={cancel} disabled={actingPlan === "cancel"}>
              {actingPlan === "cancel" ? t("subscriptions.canceling") : t("subscriptions.cancelPlan")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Plans grid */}
      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((plan) => {
          const Icon = PLAN_ICONS[plan.code] ?? Sparkles;
          const isCurrent = plan.code === myPlanCode;
          const isPopular = plan.code === "merchant";
          const price = billingCycle === "annual" ? plan.priceAnnual : plan.priceMonthly;
          const features = parseFeatures(plan.features);
          const tone = PLAN_TONES[plan.code] ?? "border-border";
          return (
            <Card key={plan.id} className={`relative flex flex-col ${tone} ${isPopular ? "md:scale-[1.02]" : ""}`}>
              {isPopular && (
                <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                  {t("subscriptions.mostPopular")}
                </Badge>
              )}
              <CardContent className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-bold">{plan.name}</p>
                    {plan.description && (
                      <p className="text-xs text-muted-foreground">{plan.description}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <span className="text-3xl font-extrabold text-gold">
                    {price === 0 ? t("common.free") : `${price}`}
                  </span>
                  {price > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {billingCycle === "annual"
                        ? t("subscriptions.perYear")
                        : t("subscriptions.perMonth")}
                    </span>
                  )}
                </div>

                {plan.commissionDiscount > 0 && (
                  <Badge variant="secondary" className="mt-2 w-fit">
                    −{plan.commissionDiscount}% {t("admin.dashboard.commission").toLowerCase()}
                  </Badge>
                )}

                <div className="mt-4 flex-1 space-y-2">
                  {features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5">
                  {isCurrent ? (
                    <Button variant="outline" className="w-full" disabled>
                      {t("subscriptions.currentPlan")}
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      variant={plan.code === "enterprise" ? "outline" : "default"}
                      onClick={() => subscribe(plan.code)}
                      disabled={actingPlan !== null}
                    >
                      {actingPlan === plan.code
                        ? t("subscriptions.subscribing")
                        : myPlanCode !== "free" && plan.code !== "free"
                          ? t("subscriptions.upgrade")
                          : plan.code === "free"
                            ? t("subscriptions.downgrade")
                            : t("subscriptions.subscribe")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {plans.length === 0 && (
        <EmptyState
          icon={<Crown className="h-8 w-8" />}
          title={t("subscriptions.plansTitle")}
          description={t("subscriptions.plansSubtitle")}
        />
      )}

      <p className="text-center text-xs text-muted-foreground">{t("subscriptions.plansSubtitle")}</p>
    </div>
  );
}

function parseFeatures(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}
